import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    PanelLeft,
    MessageSquare,
    // BarChart3,
    PenTool,
    Settings,
    User,
    LogOut,
    ArrowLeft,
    ArrowRight
} from 'lucide-react';
import messageService from '../Services/messageService';

// Cache des conversations
const CACHE_KEY = 'finai_conversations_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const saveConversationsCache = (conversations, userEmail) => {
    const cacheData = {
        conversations,
        userEmail,
        timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    console.log('💾 Cache conversations sauvegardé pour:', userEmail);
};

const loadConversationsCache = (userEmail) => {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return null;

        const cacheData = JSON.parse(cached);

        // Vérifier que c'est le bon utilisateur
        if (cacheData.userEmail !== userEmail) {
            localStorage.removeItem(CACHE_KEY);
            return null;
        }

        // Vérifier que le cache n'est pas expiré
        if (Date.now() - cacheData.timestamp > CACHE_DURATION) {
            localStorage.removeItem(CACHE_KEY);
            console.log('🗑️ Cache conversations expiré');
            return null;
        }

        console.log('📦 Cache conversations chargé pour:', userEmail);
        return cacheData.conversations;
    } catch (error) {
        console.error('Erreur cache conversations:', error);
        localStorage.removeItem(CACHE_KEY);
        return null;
    }
};

const clearConversationsCache = () => {
    localStorage.removeItem(CACHE_KEY);
    console.log('🗑️ Cache conversations nettoyé');
};

const Sidebar = ({ user, onLogout, onNewChat, onLoadConversation, currentConversationId, refreshTrigger }) => {
    // Initialiser isExpanded avec la valeur du localStorage ou false par défaut
    const [isExpanded, setIsExpanded] = useState(() => {
        const savedState = localStorage.getItem('sidebar-expanded');
        return savedState ? JSON.parse(savedState) : false;
    });
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isToggleHovered, setIsToggleHovered] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [loadingConversations, setLoadingConversations] = useState(false);
    const [conversationsLoaded, setConversationsLoaded] = useState(false);

    // ✅ AJOUT: Tracker l'email de l'utilisateur pour détecter les vrais changements
    const [lastUserEmail, setLastUserEmail] = useState(null);

    const userMenuRef = useRef(null);

    const menuItems = [
        { icon: MessageSquare, label: "New Chat", action: onNewChat },
        //{ icon: BarChart3, label: "Analytics", action: () => console.log("Analytics") },
        { icon: PenTool, label: "Tools", action: () => console.log("Tools") },
        { icon: Settings, label: "Settings", action: () => console.log("Settings") }
    ];

    const loadConversations = useCallback(async (forceRefresh = false) => {
        console.log('🚀 loadConversations appelée, forceRefresh:', forceRefresh);

        // Essayer le cache en premier (sauf si refresh forcé)
        if (!forceRefresh && user?.email) {
            const cachedConversations = loadConversationsCache(user.email);
            if (cachedConversations) {
                console.log('📋 Conversations chargées depuis le cache:', cachedConversations.length);
                setConversations(cachedConversations);
                setConversationsLoaded(true);
                return; // Sortir ici, pas d'appel API
            }
        }

        // Sinon, appel API normal
        try {
            setLoadingConversations(true);
            console.log('🌐 Appel API pour conversations');
            const conversationsList = await messageService.getConversations();
            console.log('📋 Conversations récupérées depuis API:', conversationsList.length);

            setConversations(conversationsList);
            setConversationsLoaded(true);

            // Sauvegarder dans le cache
            if (user?.email) {
                saveConversationsCache(conversationsList, user.email);
            }

        } catch (error) {
            console.error('Erreur lors du chargement des conversations:', error);
            setConversations([]);
        } finally {
            setLoadingConversations(false);
        }
    }, [user?.email]);

    // Détecter si on est sur mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
            if (window.innerWidth < 768) {
                setIsExpanded(false);
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Effet pour restaurer l'état de la sidebar au chargement (seulement sur desktop)
    useEffect(() => {
        if (window.innerWidth >= 768) {
            const savedState = localStorage.getItem('sidebar-expanded');
            if (savedState !== null) {
                setIsExpanded(JSON.parse(savedState));
            }
        }
    }, []);

    // ✅ CORRIGÉ: Gestion intelligente du changement d'utilisateur
    useEffect(() => {
        const currentUserEmail = user?.email;

        console.log('🔧 User change check:', {
            currentUserEmail,
            lastUserEmail,
            isNewUser: currentUserEmail !== lastUserEmail
        });

        // Reset seulement si c'est vraiment un nouvel utilisateur (changement d'email)
        if (currentUserEmail && currentUserEmail !== lastUserEmail) {
            console.log('👤 NOUVEAU utilisateur détecté, nettoyage cache');
            clearConversationsCache(); // Nettoyer l'ancien cache
            setConversationsLoaded(false);
            setConversations([]);
            setLastUserEmail(currentUserEmail);
        } else if (currentUserEmail && !lastUserEmail) {
            // Premier chargement - pas de nettoyage de cache
            setLastUserEmail(currentUserEmail);
        } else if (!currentUserEmail) {
            // Déconnexion
            clearConversationsCache(); // Nettoyer à la déconnexion
            setConversationsLoaded(false);
            setConversations([]);
            setLastUserEmail(null);
        }
    }, [user?.email, lastUserEmail]); // ← Surveiller seulement l'email, pas tout l'objet user

    // ✅ CORRIGÉ: Charger conversations - dependencies simplifiées
    useEffect(() => {
        console.log('🔧 useEffect CONVERSATIONS:', {
            isExpanded,
            user: !!user,
            conversationsLoaded,
            shouldLoad: isExpanded && user && !conversationsLoaded
        });

        if (isExpanded && user && !conversationsLoaded) {
            console.log('📥 TRIGGER: Chargement conversations');
            loadConversations();
        }
    }, [isExpanded, user?.email, conversationsLoaded]); // ← Pas loadingConversations, utiliser user?.email

    // ✅ CORRIGÉ: Refresh trigger - dependencies simplifiées
    useEffect(() => {
        console.log('🔧 useEffect REFRESH:', {
            refreshTrigger,
            isExpanded,
            user: !!user,
            conversationsLoaded
        });

        if (refreshTrigger > 0 && isExpanded && user && conversationsLoaded) {
            console.log('🔄 TRIGGER: Refresh conversations');
            loadConversations();
        }
    }, [refreshTrigger]); // ← Seulement refreshTrigger ! Les autres conditions sont stables

    const toggleSidebar = () => {
        const newExpandedState = !isExpanded;
        setIsExpanded(newExpandedState);
        localStorage.setItem('sidebar-expanded', JSON.stringify(newExpandedState));
        if (isExpanded) {
            setIsUserMenuOpen(false);
        }
    };

    const handleUserMenuToggle = () => {
        setIsUserMenuOpen(!isUserMenuOpen);
    };

    const handleLogout = () => {
        setIsUserMenuOpen(false);
        if (onLogout) {
            onLogout();
        }
    };

    const handleProfile = () => {
        return;
    };

    const handleSettings = () => {
        return;
    };

    const handleConversationClick = (conversationId) => {
        if (onLoadConversation) {
            onLoadConversation(conversationId);
        }
    };

    const getUserInitials = () => {
        if (!user?.email) return 'US';
        const email = user.email;
        const parts = email.split('@')[0];
        return parts.length >= 2 ? parts.substring(0, 2).toUpperCase() : parts.charAt(0).toUpperCase() + 'S';
    };

    const formatConversationTitle = (conversation) => {
        const userMessage = conversation.messages.find(msg => msg.role === 'user');
        if (userMessage) {
            return userMessage.content.length > 40
                ? userMessage.content.substring(0, 40) + '...'
                : userMessage.content;
        }
        return `Conversation ${conversation.id.substring(0, 8)}`;
    };

    // Fermer le menu utilisateur en cliquant à l'extérieur
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getToggleIcon = () => {
        if (!isToggleHovered) {
            return <PanelLeft size={20} />;
        }
        return isExpanded ? <ArrowLeft size={20} /> : <ArrowRight size={20} />;
    };

    // Mode mobile : bouton flottant uniquement
    if (isMobile && !isExpanded) {
        return (
            <button
                onClick={toggleSidebar}
                className="fixed top-1 left-1 z-50 p-3 bg-dark-bg hover:bg-dark-secondary shadow-lg transition-all duration-200 hover:scale-105"
                title="Ouvrir le menu"
            >
                <PanelLeft size={20} className="text-white" />
            </button>
        );
    }

    return (
        <>
            {/* Overlay pour mobile */}
            {isExpanded && isMobile && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
                    onClick={() => setIsExpanded(false)}
                />
            )}

            {/* Sidebar */}
            <div className={`
                ${isMobile ? 'fixed' : 'relative'} left-0 top-0 h-full bg-dark-bg text-white flex flex-col border-r border-dark-border
                ${isExpanded ? 'w-64' : 'w-16'}
                ${isMobile ? 'z-50' : 'z-10'}
                ${isMobile && isExpanded ? 'animate-slide-in-left' : ''}
                ${isMobile && !isExpanded ? 'animate-slide-out-left' : ''}
            `}
                style={{
                    transition: isMobile ? 'none' : 'width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    minWidth: isExpanded ? '256px' : '58px',
                    maxWidth: isExpanded ? '256px' : '58px',
                    transform: isMobile && !isExpanded ? 'translateX(-100%)' : 'translateX(0)',
                }}>

                {/* Header avec bouton toggle */}
                <div className="flex items-center p-4 flex-shrink-0" style={{ height: '49px' }}>
                    <button
                        onClick={toggleSidebar}
                        onMouseEnter={() => setIsToggleHovered(true)}
                        onMouseLeave={() => setIsToggleHovered(false)}
                        className="p-1 hover:bg-dark-secondary rounded-md transition-all duration-200 flex-shrink-0"
                        style={{ width: '30px', height: '30px', minWidth: '30px' }}
                        title={isToggleHovered ? (isExpanded ? "Réduire la sidebar" : "Étendre la sidebar") : (isExpanded ? "Réduire" : "Étendre")}
                    >
                        <div className="transition-transform duration-200 ease-in-out">
                            {getToggleIcon()}
                        </div>
                    </button>

                    {isExpanded && (
                        <div className="text-lg font-semibold flex-1 ml-3 transition-opacity duration-300 opacity-100">
                            LAMIEN
                        </div>
                    )}
                </div>

                {/* Menu principal */}
                <div className="py-4 flex-shrink-0">
                    <div className="px-2 space-y-2">
                        {menuItems.map((item, index) => (
                            <button
                                key={index}
                                onClick={item.action}
                                className="w-full flex items-center rounded-md hover:bg-dark-secondary group"
                                style={{
                                    height: '32px',
                                    padding: '12px',
                                    justifyContent: 'flex-start'
                                }}
                                title={!isExpanded ? item.label : ''}
                            >
                                <div className="flex items-center justify-center" style={{ width: '20px', minWidth: '20px' }}>
                                    <item.icon
                                        size={20}
                                        className="text-gray-300 group-hover:text-white"
                                    />
                                </div>
                                {isExpanded && (
                                    <span className="ml-3 text-gray-300 group-hover:text-white text-sm font-medium transition-opacity duration-300 opacity-100">
                                        {item.label}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Historique des conversations */}
                <div className="flex-1 min-h-0">
                    {isExpanded && (
                        <div className="h-full px-2">
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Conversations
                            </div>
                            <div className="overflow-y-auto space-y-1"
                                style={{
                                    height: 'calc(100% - 40px)',
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#4b5563 transparent'
                                }}>

                                {loadingConversations ? (
                                    <div className="text-center py-4">
                                        <div className="text-xs text-gray-400">Chargement...</div>
                                    </div>
                                ) : conversations.length === 0 ? (
                                    <div className="text-center py-4">
                                        <div className="text-xs text-gray-400">Aucune conversation</div>
                                    </div>
                                ) : (
                                    conversations.map((conversation) => (
                                        <button
                                            key={conversation.id}
                                            className={`w-full text-left rounded-md group transition-all duration-200 ${currentConversationId === conversation.id
                                                ? 'bg-blue-600/20 border border-blue-500/30'
                                                : 'hover:bg-dark-secondary'
                                                }`}
                                            style={{ minHeight: '35px', padding: '8px 12px' }}
                                            onClick={() => handleConversationClick(conversation.id)}
                                            title={formatConversationTitle(conversation)}
                                        >
                                            <div className="flex items-center">
                                                <div className="flex-1 min-w-0">
                                                    <div className={`text-sm font-medium truncate ${currentConversationId === conversation.id
                                                        ? 'text-blue-400'
                                                        : 'text-gray-300 group-hover:text-white'
                                                        }`}>
                                                        {formatConversationTitle(conversation)}
                                                    </div>
                                                    <div className="text-xs text-gray-500 truncate">
                                                        {new Date(conversation.updated_at).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* User menu en bas */}
                <div className={isExpanded ? 'border-t border-dark-border p-2 flex-shrink-0' : 'p-2 flex-shrink-0'}>
                    <div className="relative" ref={userMenuRef}>
                        <button
                            onClick={handleUserMenuToggle}
                            className="w-full flex items-center rounded-md hover:bg-dark-secondary"
                            style={{
                                height: '48px',
                                padding: '12px',
                                justifyContent: 'flex-start'
                            }}
                            title={!isExpanded ? user?.email : ''}
                        >
                            <div className="flex items-center justify-center" style={{ width: '32px', minWidth: '32px' }}>
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full 
                                              flex items-center justify-center text-white text-xs font-semibold">
                                    {getUserInitials()}
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="ml-3 text-left transition-opacity duration-300 opacity-100">
                                    <div className="text-sm text-gray-300 truncate max-w-32 font-medium">
                                        {user?.email?.split('@')[0] || 'Utilisateur'}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        Free Plan
                                    </div>
                                </div>
                            )}
                        </button>

                        {/* Menu déroulant utilisateur */}
                        {isUserMenuOpen && (
                            <div className="absolute bottom-full left-0 mb-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                                <div className="px-4 py-3 border-b border-gray-100">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {user?.email?.split('@')[0] || 'Utilisateur'}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {user?.email}
                                    </p>
                                </div>
                                <div className="py-1">
                                    <button
                                        onClick={handleProfile}
                                        disabled
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-400 cursor-not-allowed opacity-50"
                                        title="Profil - Bientôt disponible"
                                    >
                                        <User size={16} className="mr-3 text-gray-400" />
                                        Mon profil
                                        <span className="ml-auto text-xs text-gray-400">(Bientôt)</span>
                                    </button>

                                    <button
                                        onClick={handleSettings}
                                        disabled
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-400 cursor-not-allowed opacity-50"
                                        title="Paramètres - Bientôt disponible"
                                    >
                                        <Settings size={16} className="mr-3 text-gray-400" />
                                        Paramètres
                                        <span className="ml-auto text-xs text-gray-400">(Bientôt)</span>
                                    </button>

                                    <div className="border-t border-gray-100 my-1"></div>

                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                    >
                                        <LogOut size={16} className="mr-3 text-red-500" />
                                        Se déconnecter
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Styles pour les animations et la scrollbar */}
            <style jsx>{`
                .overflow-y-auto::-webkit-scrollbar {
                    width: 4px;
                }
                .overflow-y-auto::-webkit-scrollbar-track {
                    background: transparent;
                }
                .overflow-y-auto::-webkit-scrollbar-thumb {
                    background: #4b5563;
                    border-radius: 2px;
                }
                .overflow-y-auto::-webkit-scrollbar-thumb:hover {
                    background: #6b7280;
                }
                
                @keyframes slideInLeft {
                    from {
                        transform: translateX(-100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                
                @keyframes slideOutLeft {
                    from {
                        transform: translateX(0);
                        opacity: 1;
                    }
                    to {
                        transform: translateX(-100%);
                        opacity: 0;
                    }
                }
                
                .animate-slide-in-left {
                    animation: slideInLeft 0.3s ease-out;
                }
                
                .animate-slide-out-left {
                    animation: slideOutLeft 0.3s ease-in;
                }
            `}</style>
        </>
    );
};

export default Sidebar;